/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    SimpleLoginFormData,
    LoginResponseData,
    MisLoginFormData,
    MisLoginResponseData,
    MisAuthStatusResponseData,
    InspectorLoginParams,
    UiType,
} from '../api/auth/authClient.typings';
import { Notification } from '../../utils/utils';
import { createAction } from '../../utils/redux-utils';
import { LocaleId } from '../translation/i18n';
import { RouteId, RouteParam } from '../../router/routes';

export enum UserActionType {
    LOGIN_LOADING = 'USER/ login loading',
    LOGIN_SUCCESS = 'USER/ login success',
    LOGIN_ERROR = 'USER/ login error',
    LOGOUT_USER = 'USER/ logout user',
    CHANGE_LOCALE = 'USER/ change locale',
    LOGIN_USER = 'USER/ login',
    MIS_SEND_USER_INFO = 'USER/ mis user info send',
    MIS_USER_INFO_SUCCESS = 'USER/ mis user info success',
    MIS_AUTH_STATUS_LOAD = 'USER/ mis auth status load',
    MIS_RESET = 'USER/ mis reset',
    MIS_AUTH_COMPLETE_START = 'USER/ mis auth complete start',
    MIS_AUTH_COMPLETE_SUCCESS = 'USER/ mis auth complete success',
    SET_MIS_NOTIFICATION = 'USER/ mis set notification',
    SAVE_LAST_MONITORING_ROUTE = 'USER/ save last monitoring route',
    VALIDATE_USER_ACTION = 'USER/ validate user action',
    COMPLETE_VALIDATE_USER_ACTION = 'USER/ complete validate user action',
}

export const loginSuccess = (payload: LoginResponseData) => createAction(UserActionType.LOGIN_SUCCESS, payload);

export const loginError = (notification: Notification) => createAction(UserActionType.LOGIN_ERROR, { notification });

export const loginLoading = () => createAction(UserActionType.LOGIN_LOADING);

export const logoutUser = () => {
    return createAction(UserActionType.LOGOUT_USER);
};

export const changeLocale = (localeId: LocaleId) => {
    return createAction(UserActionType.CHANGE_LOCALE, localeId);
};

export const authorizeUser = (formData: SimpleLoginFormData) => {
    return createAction(UserActionType.LOGIN_USER, formData);
};

export const misAuthCompleteAction = (taxNumber: string, uiType: UiType) => {
    return createAction(UserActionType.MIS_AUTH_COMPLETE_START, { taxNumber, uiType });
};

export const misAuthCompleteSuccessAction = (payload: LoginResponseData) => {
    return createAction(UserActionType.MIS_AUTH_COMPLETE_SUCCESS, payload);
};

export const misSendUserInfo = (formData: MisLoginFormData, uiType: UiType) => {
    return createAction(UserActionType.MIS_SEND_USER_INFO, { formData, uiType });
};

export const misUserInfoSuccess = (payload: MisLoginResponseData) => {
    return createAction(UserActionType.MIS_USER_INFO_SUCCESS, payload);
};

export const misReset = () => createAction(UserActionType.MIS_RESET);

export const misAuthDataLoad = (authData: MisAuthStatusResponseData) => {
    return createAction(UserActionType.MIS_AUTH_STATUS_LOAD, authData);
};

export const misNotificationAction = (notification: Notification) => {
    return createAction(UserActionType.SET_MIS_NOTIFICATION, { notification });
};

export const saveLastMonitoringRouteIdAction = (payload: { routeId: RouteId; params: Record<RouteParam, string> }) => {
    return createAction(UserActionType.SAVE_LAST_MONITORING_ROUTE, payload);
};

export const validateUserAction = (userParams: InspectorLoginParams) => {
    return createAction(UserActionType.VALIDATE_USER_ACTION, userParams);
};

export const completeValidateUserAction = () => {
    return createAction(UserActionType.COMPLETE_VALIDATE_USER_ACTION);
};

export type UserAction =
    | ReturnType<typeof loginSuccess>
    | ReturnType<typeof loginError>
    | ReturnType<typeof loginLoading>
    | ReturnType<typeof changeLocale>
    | ReturnType<typeof logoutUser>
    | ReturnType<typeof authorizeUser>
    | ReturnType<typeof misReset>
    | ReturnType<typeof misSendUserInfo>
    | ReturnType<typeof misUserInfoSuccess>
    | ReturnType<typeof misAuthDataLoad>
    | ReturnType<typeof misAuthCompleteAction>
    | ReturnType<typeof misAuthCompleteSuccessAction>
    | ReturnType<typeof misNotificationAction>
    | ReturnType<typeof saveLastMonitoringRouteIdAction>
    | ReturnType<typeof validateUserAction>
    | ReturnType<typeof completeValidateUserAction>;
